"use client";

import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "./api/axios";
import { getImageUrl } from "./utils/image";
import { useActivityLogger } from "./hooks/useActivity";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";
import { CircleCheck, EllipsisVertical, Pencil, Trash } from "lucide-react";
import {
  EditThreadModal,
  DeleteThreadModal,
  EditReplyModal,
} from "./components/modals/ThreadModals";

export default function DiscussionDetails() {
  const { topicId, id } = useParams();
  const { user, loading: loadingUser } = useAuth();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [usePseudonym, setUsePseudonym] = useState(false);
  const [replyingToReplyId, setReplyingToReplyId] = useState(null);
  const [replyForms, setReplyForms] = useState({});
  const [likingReplyId, setLikingReplyId] = useState(null);
  const [localSelectedImage, setLocalSelectedImage] = useState(null);
  const [localImagePreview, setLocalImagePreview] = useState(null);
  const localFileInputRef = useRef(null);
  const { logUserActivity, loading: userLoading } = useActivityLogger();
  const utmSource = searchParams.get("utm_source");
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState("");

  // Edit/Delete modal states for thread
  const [showEditThreadModal, setShowEditThreadModal] = useState(false);
  const [showDeleteThreadModal, setShowDeleteThreadModal] = useState(false);
  const [isEditingThread, setIsEditingThread] = useState(false);
  const [isDeletingThread, setIsDeletingThread] = useState(false);

  // Edit/Delete modal states for replies
  const [showEditReplyModal, setShowEditReplyModal] = useState(false);
  const [showDeleteReplyModal, setShowDeleteReplyModal] = useState(false);
  const [selectedReply, setSelectedReply] = useState(null);
  const [isEditingReply, setIsEditingReply] = useState(false);
  const [isDeletingReply, setIsDeletingReply] = useState(false);

  const [userRoles, setUserRoles] = useState({});
  useEffect(() => {
    if (utmSource && !userLoading) {
      logUserActivity(
        "utm_tracking",
        "page_visit_with_utm",
        "discussion_details",
        `User visited from UTM source: ${utmSource}`
      );
    }
  }, [utmSource, userLoading]);

  useEffect(() => {
    const fetchThread = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/forum/threads/${id}`);
        setThread(response.data);
        if (!userLoading) {
          logUserActivity(
            "user",
            "view_thread_details",
            "DiscussionDetails",
            `User viewed thread: ${response.data.title}`
          );
        }
      } catch (err) {
        console.error("Failed to fetch thread:", err);
        if (!userLoading) {
          logUserActivity(
            "error",
            "fetch_thread_error",
            "DiscussionDetails",
            `Error fetching thread: ${id} - ${err.message}`
          );
        }
        setError("Failed to load discussion");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchThread();
    }
  }, [id, userLoading]);
  useEffect(() => {
    const fetchUserRoles = async () => {
      const roles = {};
      const usersToFetch = new Set();

      // Add thread author
      if (thread?.user?.ID) {
        usersToFetch.add(thread.user.ID);
      }

      // Add all reply authors
      if (thread?.Replies) {
        thread.Replies.forEach((reply) => {
          if (reply.user?.ID) {
            usersToFetch.add(reply.user.ID);
          }
        });
      }

      // Fetch roles for all unique users
      for (const userId of usersToFetch) {
        try {
          const response = await api.get(`/users/${userId}/roles`);
          roles[userId] = response.data[0]?.name || "";
        } catch (error) {
          console.error("Failed to fetch user data:", error);
          roles[userId] = "";
        }
      }
      setUserRoles(roles);
    };

    if (
      thread &&
      (thread.user?.ID || (thread.Replies && thread.Replies.length > 0))
    ) {
      fetchUserRoles();
    }
  }, [thread]);
  const alertNotLoggedIn = () => {
    alert("Anda harus login untuk melakukan tindakan ini.");
    logUserActivity(
      "alert",
      "not_logged_in",
      "forum_topic",
      "user_not_logged_in_action"
    );
  };

  const openImageModal = (imageSrc) => {
    setModalImageSrc(imageSrc);
    setImageModalOpen(true);
  };

  const closeImageModal = () => {
    setImageModalOpen(false);
    setModalImageSrc("");
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        closeImageModal();
      }
    };

    if (imageModalOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [imageModalOpen]);

  // Check if item is within 15 minutes for edit/delete
  const isWithin15Minutes = (createdAt) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffInMinutes = (now - created) / (1000 * 60);
    return diffInMinutes <= 15;
  };

  // Thread edit/delete functions
  const openEditThreadModal = () => {
    if (!loadingUser && !user) {
      alertNotLoggedIn();
      return;
    }
    setShowEditThreadModal(true);
    logUserActivity(
      "click",
      "open_edit_thread_modal",
      "discussion_details",
      `open_edit_thread_modal thread_id=${thread.ID}`
    );
  };

  const closeEditThreadModal = () => {
    setShowEditThreadModal(false);
    setIsEditingThread(false);
    logUserActivity(
      "click",
      "close_edit_thread_modal",
      "discussion_details",
      "close_edit_thread_modal"
    );
  };

  const handleEditThreadSave = async ({
    title,
    content,
    use_pseudonym,
    imageFile,
  }) => {
    if (!thread) return;

    try {
      setIsEditingThread(true);

      const formData = new FormData();
      formData.append("title", title);
      formData.append("content", content);
      formData.append("use_pseudonym", use_pseudonym.toString());
      if (imageFile) {
        formData.append("image", imageFile);
      }

      await api.put(`/forum/threads/${thread.ID}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      logUserActivity(
        "submit",
        "edit_thread_form",
        "discussion_details",
        `edit_thread thread_id=${thread.ID}`
      );

      // Refresh thread
      const response = await api.get(`/forum/threads/${id}`);
      setThread(response.data);

      closeEditThreadModal();
    } catch (err) {
      console.error("Failed to edit thread:", err);
      logUserActivity(
        "error",
        "edit_thread_form",
        "discussion_details",
        `Failed to edit thread ${thread.ID}: ${err.message}`
      );
      alert("Gagal mengedit thread. Silakan coba lagi.");
    } finally {
      setIsEditingThread(false);
    }
  };

  const openDeleteThreadModal = () => {
    if (!loadingUser && !user) {
      alertNotLoggedIn();
      return;
    }
    setShowDeleteThreadModal(true);
    logUserActivity(
      "click",
      "open_delete_thread_modal",
      "discussion_details",
      `open_delete_thread_modal thread_id=${thread.ID}`
    );
  };

  const closeDeleteThreadModal = () => {
    setShowDeleteThreadModal(false);
    setIsDeletingThread(false);
    logUserActivity(
      "click",
      "close_delete_thread_modal",
      "discussion_details",
      "close_delete_thread_modal"
    );
  };

  const handleDeleteThreadConfirm = async () => {
    if (!thread) return;

    try {
      setIsDeletingThread(true);

      await api.delete(`/forum/threads/${thread.ID}`);

      logUserActivity(
        "click",
        "delete_thread_button",
        "discussion_details",
        `delete_thread thread_id=${thread.ID}`
      );

      // Navigate back to forum topic
      navigate(`/forum/${topicId}`);
    } catch (err) {
      console.error("Failed to delete thread:", err);
      logUserActivity(
        "error",
        "delete_thread_button",
        "discussion_details",
        `delete_thread error for thread_id=${thread.ID}: ${err.message}`
      );
      alert("Gagal menghapus thread. Silakan coba lagi.");
    } finally {
      setIsDeletingThread(false);
    }
  };

  // Reply edit/delete functions
  const openEditReplyModal = (reply) => {
    if (!loadingUser && !user) {
      alertNotLoggedIn();
      return;
    }
    setSelectedReply(reply);
    setShowEditReplyModal(true);
    logUserActivity(
      "click",
      "open_edit_reply_modal",
      "discussion_details",
      `open_edit_reply_modal reply_id=${reply.ID}`
    );
  };

  const closeEditReplyModal = () => {
    setShowEditReplyModal(false);
    setSelectedReply(null);
    setIsEditingReply(false);
    logUserActivity(
      "click",
      "close_edit_reply_modal",
      "discussion_details",
      "close_edit_reply_modal"
    );
  };

  const handleEditReplySave = async ({
    title,
    content,
    use_pseudonym,
    imageFile,
  }) => {
    if (!selectedReply) return;

    try {
      setIsEditingReply(true);

      const formData = new FormData();
      formData.append("content", content);
      formData.append("use_pseudonym", use_pseudonym.toString());
      if (imageFile) {
        formData.append("image", imageFile);
      }

      await api.put(`/forum/replies/${selectedReply.ID}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      logUserActivity(
        "submit",
        "edit_reply_form",
        "discussion_details",
        `edit_reply reply_id=${selectedReply.ID}`
      );

      // Refresh thread
      const response = await api.get(`/forum/threads/${id}`);
      setThread(response.data);

      closeEditReplyModal();
    } catch (err) {
      console.error("Failed to edit reply:", err);
      logUserActivity(
        "error",
        "edit_reply_form",
        "discussion_details",
        `Failed to edit reply ${selectedReply.ID}: ${err.message}`
      );
      alert("Gagal mengedit reply. Silakan coba lagi.");
    } finally {
      setIsEditingReply(false);
    }
  };

  const openDeleteReplyModal = (reply) => {
    if (!loadingUser && !user) {
      alertNotLoggedIn();
      return;
    }
    setSelectedReply(reply);
    setShowDeleteReplyModal(true);
    logUserActivity(
      "click",
      "open_delete_reply_modal",
      "discussion_details",
      `open_delete_reply_modal reply_id=${reply.ID}`
    );
  };

  const closeDeleteReplyModal = () => {
    setShowDeleteReplyModal(false);
    setSelectedReply(null);
    setIsDeletingReply(false);
    logUserActivity(
      "click",
      "close_delete_reply_modal",
      "discussion_details",
      "close_delete_reply_modal"
    );
  };

  const handleDeleteReplyConfirm = async () => {
    if (!selectedReply) return;

    try {
      setIsDeletingReply(true);

      await api.delete(`/forum/replies/${selectedReply.ID}`);

      logUserActivity(
        "click",
        "delete_reply_button",
        "discussion_details",
        `delete_reply reply_id=${selectedReply.ID}`
      );

      // Refresh thread
      const response = await api.get(`/forum/threads/${id}`);
      setThread(response.data);

      closeDeleteReplyModal();
    } catch (err) {
      console.error("Failed to delete reply:", err);
      logUserActivity(
        "error",
        "delete_reply_button",
        "discussion_details",
        `delete_reply error for reply_id=${selectedReply.ID}: ${err.message}`
      );
      alert("Gagal menghapus reply. Silakan coba lagi.");
    } finally {
      setIsDeletingReply(false);
    }
  };

  const handleLikeThread = async () => {
    if (!loadingUser && !user) {
      alertNotLoggedIn();
      return;
    }
    try {
      await api.post(
        `/forum/threads/${id}/like`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const response = await api.get(`/forum/threads/${id}`);
      logUserActivity(
        "user",
        "like_thread",
        "DiscussionDetails",
        `User liked thread: ${response.data.title}`
      );
      setThread(response.data);
    } catch (err) {
      logUserActivity(
        "user",
        "like_thread_error",
        "DiscussionDetails",
        `Error liking thread: ${id} - ${err.message}`
      );
      console.error("Failed to like/unlike thread:", err);
      alert("Failed to update like status. Please try again.");
    }
  };

  const handleLikeReply = async (replyId) => {
    if (!loadingUser && !user) {
      alertNotLoggedIn();
      return;
    }
    if (likingReplyId === replyId) return;

    setLikingReplyId(replyId);
    try {
      await api.post(
        `/forum/replies/${replyId}/like`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const response = await api.get(`/forum/threads/${id}`);
      logUserActivity(
        "user",
        "like_reply",
        "DiscussionDetails",
        `User liked reply ID: ${replyId} in thread: ${id}`
      );

      setThread(response.data);
    } catch (err) {
      logUserActivity(
        "user",
        "like_reply_error",
        "DiscussionDetails",
        `Error liking reply ID: ${replyId} in thread: ${id} - ${err.message}`
      );
      console.error("Failed to like/unlike reply:", err);
      alert("Failed to update like status. Please try again.");
    } finally {
      setLikingReplyId(null);
    }
  };
  const getAuthorId = (thread) => {
    return thread.user ? thread.user.ID : null;
  };

  const formatTime = (dateString) => {
    if (!dateString) return "Tanggal tidak diketahui";

    const date = new Date(dateString);
    const now = new Date();

    // Reset time to compare dates only
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const articleDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );

    const diffTime = today - articleDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    const months = [
      "Januari",
      "Februari",
      "Maret",
      "April",
      "Mei",
      "Juni",
      "Juli",
      "Agustus",
      "September",
      "Oktober",
      "November",
      "Desember",
    ];

    const days = [
      "Minggu",
      "Senin",
      "Selasa",
      "Rabu",
      "Kamis",
      "Jumat",
      "Sabtu",
    ];

    // Jika hari ini, tampilkan jam saja
    if (diffDays === 0) {
      return date.toLocaleTimeString("id-ID", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    }

    // Jika dalam seminggu terakhir, tampilkan hari dan tanggal
    if (diffDays > 0 && diffDays <= 7) {
      const dayName = days[date.getDay()];
      const day = date.getDate();
      const month = months[date.getMonth()];
      return `${dayName}, ${day} ${month}`;
    }

    // Jika tahun yang sama, tampilkan tanggal bulan saja
    if (date.getFullYear() === now.getFullYear()) {
      const day = date.getDate();
      const month = months[date.getMonth()];
      return `${day} ${month}`;
    }

    // Jika tahun berbeda, tampilkan dengan tahun
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const handleReplyToReply = (replyId) => {
    if (!loadingUser && !user) {
      alertNotLoggedIn();
      return;
    }
    setReplyingToReplyId(replyId);
    setReplyForms((prev) => ({
      ...prev,
      [replyId]: {
        text: prev[replyId]?.text || "",
        usePseudonym: prev[replyId]?.usePseudonym || false,
      },
    }));
  };

  const handleReplyToReplySubmit = async (e, parentReplyId) => {
    if (!loadingUser && !user) {
      alertNotLoggedIn();
      return;
    }
    e.preventDefault();
    const replyData = replyForms[parentReplyId];
    if (!replyData?.text.trim()) return;

    try {
      const formData = new FormData();
      formData.append("content", replyData.text);
      formData.append("use_pseudonym", replyData.usePseudonym.toString());
      formData.append("parent_id", parentReplyId);

      await api.post(`/forum/threads/${id}/reply`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const response = await api.get(`/forum/threads/${id}`);
      logUserActivity(
        "user",
        "reply_to_reply",
        "DiscussionDetails",
        `User replied to reply ID: ${parentReplyId} in thread: ${id}`
      );
      setThread(response.data);
      handleCancelReplyToReply(parentReplyId);
    } catch (err) {
      logUserActivity(
        "user",
        "reply_to_reply_error",
        "DiscussionDetails",
        `Error replying to reply ID: ${parentReplyId} in thread: ${id} - ${err.message}`
      );

      console.error("Failed to post nested reply:", err);
      alert("Failed to post reply. Please try again.");
    }
  };

  const getAuthorName = (user, usePseudonym) => {
    if (user && user.ID !== 0) {
      if (usePseudonym && user.nama_samaran) {
        return user.nama_samaran;
      }
      return user.username || "Pengguna";
    }
    return "Anonim";
  };

  const getIsUserVerified = (thread) => {
    return thread.user && thread.user.is_verified;
  };

  const handleImageSelect = (e) => {
    if (!loadingUser && !user) {
      alertNotLoggedIn();
      return;
    }
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert("File terlalu besar. Maksimal 5MB.");
        return;
      }
      if (!file.type.startsWith("image/")) {
        alert("File harus berupa gambar.");
        return;
      }
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleCancelReplyToReply = (replyId) => {
    setReplyingToReplyId(null);
    setReplyForms((prev) => {
      const newForms = { ...prev };
      delete newForms[replyId];
      return newForms;
    });
  };

  const organizeReplies = (replies) => {
    if (!replies || replies.length === 0) return [];
    const replyMap = new Map();
    const topLevel = [];

    replies.forEach((reply) => {
      replyMap.set(reply.ID, {
        ...reply,
        nestedReplies: [],
      });
    });

    replies.forEach((reply) => {
      const replyObj = replyMap.get(reply.ID);
      if (reply.parent_id && replyMap.has(reply.parent_id)) {
        const parentReply = replyMap.get(reply.parent_id);
        parentReply.nestedReplies.push(replyObj);
      } else {
        topLevel.push(replyObj);
      }
    });

    return topLevel;
  };

  const RenderReply = ({ reply, depth = 0 }) => {
    const [localReplyText, setLocalReplyText] = useState("");
    const [localUsePseudonym, setLocalUsePseudonym] = useState(false);

    useEffect(() => {
      if (replyingToReplyId === reply.ID) {
        setLocalReplyText(replyForms[reply.ID]?.text || "");
        setLocalUsePseudonym(replyForms[reply.ID]?.usePseudonym || false);
      }
    }, [replyingToReplyId, reply.ID]);

    const handleLocalSubmit = async (e) => {
      if (!loadingUser && !user) {
        alertNotLoggedIn();
        return;
      }
      e.preventDefault();
      if (!localReplyText.trim() && !localSelectedImage) return;

      try {
        const formData = new FormData();
        formData.append("content", localReplyText);
        formData.append("use_pseudonym", localUsePseudonym.toString());
        formData.append("parent_id", reply.ID);
        if (localSelectedImage) {
          formData.append("image", localSelectedImage);
        }

        await api.post(`/forum/threads/${id}/reply`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        const response = await api.get(`/forum/threads/${id}`);
        setThread(response.data);
        handleCancelReplyToReply(reply.ID);
        setLocalReplyText("");
        setLocalUsePseudonym(false);
        removeLocalImage();
      } catch (err) {
        console.error("Failed to post nested reply:", err);
        alert("Failed to post reply. Please try again.");
      }
    };

    const handleLocalImageSelect = (e) => {
      if (!loadingUser && !user) {
        alertNotLoggedIn();
        return;
      }
      const file = e.target.files[0];
      if (file) {
        if (file.size > 5 * 1024 * 1024) {
          alert("File terlalu besar. Maksimal 5MB.");
          return;
        }
        if (!file.type.startsWith("image/")) {
          alert("File harus berupa gambar.");
          return;
        }
        setLocalSelectedImage(file);
        const reader = new FileReader();
        reader.onload = (e) => setLocalImagePreview(e.target.result);
        reader.readAsDataURL(file);
      }
    };

    const removeLocalImage = () => {
      setLocalSelectedImage(null);
      setLocalImagePreview(null);
      if (localFileInputRef.current) localFileInputRef.current.value = "";
    };

    return (
      <div
        className={`p-4 ${
          depth > 0 ? "border-l-2 border-blue-200 ml-4 pl-4 bg-blue-50/30" : ""
        }`}
      >
        <div className="flex items-start gap-3 mb-2 relative">
          {/* Edit/Delete actions for reply */}
          {/* {reply.user?.ID === user?.ID &&
            isWithin15Minutes(reply.CreatedAt) && (
              <div className="absolute top-0 right-0">
                <button
                  className="p-1 rounded-full hover:bg-gray-100 transition"
                  onClick={(e) => {
                    e.stopPropagation();
                    setThread((prev) => ({
                      ...prev,
                      Replies: prev.Replies.map((r) =>
                        r.ID === reply.ID
                          ? { ...r, showActions: !r.showActions }
                          : { ...r, showActions: false }
                      ),
                    }));
                  }}
                  aria-label="More actions"
                >
                  <EllipsisVertical className="w-4 h-4 text-gray-400" />
                </button>
                {reply.showActions && (
                  <div className="absolute right-0 mt-2 w-28 bg-white border rounded shadow-lg z-10">
                    <button
                      className="block w-full text-left px-3 py-2 text-xs hover:bg-gray-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditReplyModal(reply);
                        setThread((prev) => ({
                          ...prev,
                          Replies: prev.Replies.map((r) =>
                            r.ID === reply.ID ? { ...r, showActions: false } : r
                          ),
                        }));
                      }}
                    >
                      Edit
                    </button>
                    <button
                      className="block w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-gray-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDeleteReplyModal(reply);
                        setThread((prev) => ({
                          ...prev,
                          Replies: prev.Replies.map((r) =>
                            r.ID === reply.ID ? { ...r, showActions: false } : r
                          ),
                        }));
                      }}
                    >
                      Delete
                    </button>
                  </div>
                )} */}
          {/* </div> */}
          {/* )} */}

          <div className="flex-1">
            <p className="text-sm text-gray-800 mb-1">
              <span className="font-medium text-gray-900 flex items-center gap-1">
                {getAuthorName(reply.user, reply.use_pseudonym)}
                {getIsUserVerified(reply) && (
                  <CircleCheck className="inline-block w-4 h-4 text-blue-500 ml-1" />
                )}
                {userRoles[reply.user?.ID] && (
                  <div className="bg-slate-100 px-1 py-0.5 rounded-2xl ml-2">
                    <span className="text-black text-xs">
                      {userRoles[reply.user.ID]}
                    </span>
                  </div>
                )}
                <p className="text-xs text-gray-500 font-normal ml-2">
                  {formatTime(reply.CreatedAt)}
                </p>
              </span>
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">
              {reply.content}
            </p>
          </div>
        </div>

        {reply.image_url && (
          <div className="ml-11 mb-3">
            <img
              src={getImageUrl(reply.image_url) || "/placeholder.svg"}
              alt="Reply image"
              className="w-full max-h-64 object-cover rounded-lg border border-gray-200"
              onClick={() => openImageModal(getImageUrl(reply.image_url))}
              onError={(e) => {
                console.log("Comment image failed to load:", e.target.src);
                e.target.style.display = "none";
              }}
            />
          </div>
        )}

        <div className="flex justify-end gap-4 text-xs text-gray-500 ml-11">
          <button
            onClick={() => handleReplyToReply(reply.ID)}
            className="flex items-center gap-1 hover:text-blue-500 px-2 py-1 rounded hover:bg-blue-50"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            <span>{reply.nestedReplies?.length || 0}</span>
          </button>

          <button
            onClick={() => handleLikeReply(reply.ID)}
            disabled={likingReplyId === reply.ID}
            className={`flex items-center gap-1 hover:text-red-500 cursor-pointer px-2 py-1 rounded hover:bg-red-50 transition-colors ${
              likingReplyId === reply.ID ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {likingReplyId === reply.ID ? (
              <div className="w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <svg
                className={`w-4 h-4 transition-colors ${
                  reply.is_liked
                    ? "text-red-500 fill-current"
                    : "hover:text-red-500"
                }`}
                fill={reply.is_liked ? "currentColor" : "none"}
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                />
              </svg>
            )}
            <span className={reply.is_liked ? "text-red-500" : ""}>
              {reply.like_count || 0}
            </span>
          </button>

          {reply.user?.ID === user?.ID &&
            isWithin15Minutes(reply.CreatedAt) && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openEditReplyModal(reply);
                  }}
                  className="flex items-center gap-1 hover:text-blue-500 transition-colors px-2 py-1 rounded hover:bg-blue-50"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openDeleteReplyModal(reply);
                  }}
                  className="flex items-center gap-1 hover:text-red-500 transition-colors px-2 py-1 rounded hover:bg-red-50"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </>
            )}
        </div>

        {/* Nested reply form */}
        {replyingToReplyId === reply.ID && (
          <div className="mt-3 ml-11 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <textarea
              value={localReplyText}
              onChange={(e) => setLocalReplyText(e.target.value)}
              placeholder="Write your reply..."
              className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[72px] max-h-32"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  handleLocalSubmit(e);
                }
              }}
            />
            <div className="flex items-center gap-2 mb-2">
              <button
                type="button"
                onClick={() => localFileInputRef.current?.click()}
                className="p-2 text-gray-500 hover:text-blue-500 hover:bg-blue-50 rounded-full border border-gray-300"
                title="Tambah gambar"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </button>
              <input
                ref={localFileInputRef}
                type="file"
                accept="image/*"
                onChange={handleLocalImageSelect}
                className="hidden"
              />
              {localImagePreview && (
                <div className="flex items-center gap-2">
                  <img
                    src={localImagePreview}
                    alt="Preview"
                    className="w-12 h-12 object-cover rounded border"
                  />
                  <button
                    type="button"
                    onClick={removeLocalImage}
                    className="text-red-500 hover:text-red-700 text-xs font-medium px-2 py-1 rounded hover:bg-red-50"
                  >
                    Hapus
                  </button>
                </div>
              )}
            </div>
            <div className="flex justify-between items-center mt-3">
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={localUsePseudonym}
                  onChange={(e) => setLocalUsePseudonym(e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
                Gunakan nama samaran
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    handleCancelReplyToReply(reply.ID);
                    setLocalReplyText("");
                    setLocalUsePseudonym(false);
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleLocalSubmit}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  disabled={!localReplyText.trim()}
                >
                  Reply
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Nested replies */}
        {reply.nestedReplies && reply.nestedReplies.length > 0 && (
          <div className="mt-3 ml-11">
            {reply.nestedReplies.map((nestedReply) => (
              <RenderReply
                key={nestedReply.ID}
                reply={nestedReply}
                depth={depth + 1}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  const handleReplySubmit = async (e) => {
    if (!loadingUser && !user) {
      alertNotLoggedIn();
      return;
    }
    e.preventDefault();
    if (!replyText.trim() && !selectedImage) return;

    try {
      const formData = new FormData();
      formData.append("content", replyText || "");
      formData.append("use_pseudonym", usePseudonym.toString());
      if (selectedImage) {
        formData.append("image", selectedImage);
      }

      await api.post(`/forum/threads/${id}/reply`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const response = await api.get(`/forum/threads/${id}`);
      setThread(response.data);
      setReplyText("");
      setUsePseudonym(false);
      removeImage();
    } catch (err) {
      console.error("Failed to post reply:", err);
      alert("Failed to post reply. Please try again.: " + err.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white px-4 py-4">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => {
              logUserActivity(
                "navigation",
                "go_back_from_loading",
                "DiscussionDetails",
                "User clicked back from loading state"
              );
              navigate(`/forum/${topicId}`);
            }}
          >
            <img src="/icon-back.png" alt="Back" className="h-6 w-6" />
          </button>
          <h1 className="text-xl font-semibold">Loading...</h1>
          <div className="h-6 w-6" />
        </div>
        <div className="flex items-center justify-center mt-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
            <p className="text-gray-600">Loading discussion...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !thread) {
    return (
      <div className="min-h-screen bg-white px-4 py-4">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => {
              logUserActivity(
                "navigation",
                "go_back_from_error",
                "DiscussionDetails",
                "User clicked back from error state"
              );
              navigate(`/forum/${topicId}`);
            }}
          >
            <img src="/icon-back.png" alt="Back" className="h-6 w-6" />
          </button>
          <h1 className="text-xl font-semibold">Forum</h1>
          <div className="h-6 w-6" />
        </div>
        <div className="flex items-center justify-center mt-20">
          <div className="text-center">
            <p className="text-red-600 mb-4">
              {error || "Discussion not found"}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Edit Thread Modal */}
      <EditThreadModal
        isOpen={showEditThreadModal}
        onClose={closeEditThreadModal}
        onSave={handleEditThreadSave}
        loading={isEditingThread}
        initialTitle={thread?.title || ""}
        initialContent={thread?.content || ""}
        initialUsePseudonym={thread?.use_pseudonym || false}
        initialImageUrl={thread?.image_url ? getImageUrl(thread.image_url) : ""}
      />

      {/* Delete Thread Modal */}
      <DeleteThreadModal
        isOpen={showDeleteThreadModal}
        onClose={closeDeleteThreadModal}
        onConfirm={handleDeleteThreadConfirm}
        loading={isDeletingThread}
        threadTitle={thread?.title || ""}
      />

      {/* Edit Reply Modal */}
      <EditReplyModal
        isOpen={showEditReplyModal}
        onClose={closeEditReplyModal}
        onSave={handleEditReplySave}
        loading={isEditingReply}
        initialContent={selectedReply?.content || ""}
        initialUsePseudonym={selectedReply?.use_pseudonym || false}
        initialImageUrl={
          selectedReply?.image_url ? getImageUrl(selectedReply.image_url) : ""
        }
      />

      {/* Delete Reply Modal */}
      <DeleteThreadModal
        isOpen={showDeleteReplyModal}
        onClose={closeDeleteReplyModal}
        onConfirm={handleDeleteReplyConfirm}
        loading={isDeletingReply}
        threadTitle={
          selectedReply?.content
            ? `"${selectedReply.content.substring(0, 50)}..."`
            : ""
        }
      />

      <div className="min-h-screen bg-gray-50 px-4 py-4 pb-32">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 bg-white sticky top-0 z-30 py-4 border-b border-gray-100">
          <button
            onClick={() => {
              logUserActivity(
                "navigation",
                "go_back_from_discussion_details",
                "DiscussionDetails",
                "User clicked back from discussion details"
              );
              navigate(`/forum/${topicId}`);
            }}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <img src="/icon-back.png" alt="Back" className="h-6 w-6" />
          </button>
          <h1 className="text-xl font-semibold text-gray-800">
            {thread.category?.name || "Forum"}
          </h1>
          <div className="h-6 w-6" />
        </div>

        {/* Original Post */}
        <div className="mb-6 bg-white rounded-lg border border-gray-200 p-4 shadow-sm relative">
          {/* Edit/Delete actions for thread */}
          {/* {thread.user?.ID === user?.ID &&
            isWithin15Minutes(thread.CreatedAt) && (
              <div className="absolute top-4 right-4">
                <button
                  className="p-2 rounded-full hover:bg-gray-100 transition"
                  onClick={(e) => {
                    e.stopPropagation();
                    setThread((prev) => ({
                      ...prev,
                      showActions: !prev.showActions,
                    }));
                  }}
                  aria-label="More actions"
                >
                  <EllipsisVertical className="w-5 h-5 text-gray-400" />
                </button>
                {thread.showActions && (
                  <div className="absolute right-0 mt-2 w-32 bg-white border rounded shadow-lg z-10">
                    <button
                      className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditThreadModal();
                        setThread((prev) => ({
                          ...prev,
                          showActions: false,
                        }));
                      }}
                    >
                      Edit
                    </button>
                    <button
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDeleteThreadModal();
                        setThread((prev) => ({
                          ...prev,
                          showActions: false,
                        }));
                      }}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            )} */}

          <h2 className="font-semibold text-lg mb-2 text-gray-900 pr-12">
            {thread.title}
          </h2>
          <p className="text-xs text-gray-500 mb-3 flex items-center gap-2">
            <span className="bg-gray-100 px-2 py-1 rounded-full text-xs font-medium">
              {getAuthorName(thread.user, thread.use_pseudonym)}
              {getIsUserVerified(thread) && (
                <CircleCheck className="inline-block w-4 h-4 text-blue-500 ml-1" />
              )}
            </span>
            {userRoles[thread.user?.ID] && (
              <>
                <span>•</span>
                <div className="bg-slate-100 px-1 py-0.5 rounded-2xl">
                  <span className="text-black">
                    {userRoles[thread.user.ID]}
                  </span>
                </div>
              </>
            )}
            <span>•</span>
            <span>{formatTime(thread.CreatedAt)}</span>
          </p>
          <p className="text-sm text-gray-700 mb-4 leading-relaxed">
            {thread.content}
          </p>
          {thread.image_url && (
            <div className="mb-4">
              <img
                src={getImageUrl(thread.image_url) || "/placeholder.svg"}
                alt="Thread image"
                className="w-full max-h-64 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                onError={(e) => {
                  console.log("Thread image failed to load:", e.target.src);
                  e.target.style.display = "none";
                }}
                onClick={() => openImageModal(getImageUrl(thread.image_url))}
              />
            </div>
          )}
          <div className="flex justify-end gap-6 text-xs text-gray-500 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-1 hover:text-blue-500 cursor-pointer">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
              <span>{thread.Replies?.length || 0}</span>
            </div>

            <button
              onClick={handleLikeThread}
              className="flex items-center gap-1 hover:text-red-500 cursor-pointer transition-colors"
            >
              <svg
                className={`w-4 h-4 transition-colors ${
                  thread.is_liked
                    ? "text-red-500 fill-current"
                    : "hover:text-red-500"
                }`}
                fill={thread.is_liked ? "currentColor" : "none"}
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                />
              </svg>
              <span className={thread.is_liked ? "text-red-500" : ""}>
                {thread.like_count || 0}
              </span>
            </button>
            {thread.user?.ID === user?.ID &&
              isWithin15Minutes(thread.CreatedAt) && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditThreadModal();
                    }}
                    className="flex items-center gap-1 hover:text-blue-500 transition-colors px-2 py-1 rounded hover:bg-blue-50"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openDeleteThreadModal();
                    }}
                    className="flex items-center gap-1 hover:text-red-500 transition-colors px-2 py-1 rounded hover:bg-red-50"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </>
              )}
          </div>
        </div>

        {/* Replies */}
        {thread.Replies && thread.Replies.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="px-4 py-3 border-b border-gray-100">
              <h3 className="text-sm font-medium text-gray-700">
                Replies ({thread.Replies.length})
              </h3>
            </div>
            <div className="divide-y divide-gray-100">
              {organizeReplies(thread.Replies).map((reply) => (
                <RenderReply key={reply.ID} reply={reply} />
              ))}
            </div>
          </div>
        )}

        {/* Image Preview */}
        {imagePreview && (
          <div className="fixed bottom-28 left-4 right-4 bg-white border border-gray-300 rounded-xl p-4 shadow-lg z-10">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">
                Preview Gambar:
              </span>
              <button
                onClick={removeImage}
                className="text-red-500 hover:text-red-700 text-sm font-medium px-2 py-1 rounded hover:bg-red-50"
              >
                Hapus
              </button>
            </div>
            <img
              src={imagePreview || "/placeholder.svg"}
              alt="Preview"
              className="w-full max-h-32 object-cover rounded-lg border border-gray-200"
            />
          </div>
        )}

        {/* Main Reply Form */}
        <form
          onSubmit={handleReplySubmit}
          className={`fixed bottom-0 left-0 right-0 bg-white px-4 py-4 border-t border-gray-200 shadow-lg z-20 ${
            replyingToReplyId ? "opacity-50 pointer-events-none" : ""
          }`}
        >
          <div className="mb-3">
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={usePseudonym}
                onChange={(e) => setUsePseudonym(e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                disabled={replyingToReplyId !== null}
              />
              Gunakan nama samaran
            </label>
          </div>
          {imageModalOpen && (
            <div
              className="fixed inset-0 bg-black bg-opacity-75 backdrop-blur-lg flex items-center justify-center z-100 p-10"
              onClick={closeImageModal}
            >
              <div
                className="relative max-w-screen max-h-screen m-10"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="absolute top-2 right-2 bg-black bg-opacity-50 text-white rounded-full p-2 hover:bg-opacity-75 transition-all z-10"
                  onClick={closeImageModal}
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>

                <img
                  src={modalImageSrc}
                  alt="Full size thread image"
                  className="max-w-screen max-h-screen object-contain rounded-lg"
                  onError={(e) => {
                    console.log("Modal image failed to load:", e.target.src);
                    closeImageModal();
                  }}
                />
              </div>
            </div>
          )}

          <div className="flex items-end gap-3">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex-shrink-0 p-3 text-gray-500 hover:text-blue-500 hover:bg-blue-50 rounded-full border border-gray-300"
              title="Tambah gambar"
              disabled={replyingToReplyId !== null}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              className="hidden"
            />
            <input
              type="text"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder={
                replyingToReplyId
                  ? "Selesaikan reply di atas dulu..."
                  : "Gabung obrolan"
              }
              className="flex-1 border border-gray-300 rounded-full px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={replyingToReplyId !== null}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleReplySubmit(e);
                }
              }}
            />
            <button
              type="submit"
              disabled={
                (!replyText.trim() && !selectedImage) ||
                replyingToReplyId !== null
              }
              className="flex-shrink-0 bg-blue-500 text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed shadow-sm"
            >
              Kirim
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
